import { useState } from 'react';
import { ArrowRight, CheckCircle, X, DollarSign, Clock, Users, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import AppointmentModal from '../components/AppointmentModal';

const COMPARISON_DATA = [
  {
    factor: 'Number of Appointments',
    traditional: '4-6 visits',
    direct: '3-4 visits',
  },
  {
    factor: 'Intermediaries',
    traditional: 'Dentist + Lab + Specialist',
    direct: 'Single Provider',
  },
  {
    factor: 'Lab Markup',
    traditional: 'Yes',
    direct: 'No',
  },
  {
    factor: 'Referral Fees',
    traditional: 'Often included',
    direct: 'None',
  },
  {
    factor: 'Personalized Adjustments',
    traditional: 'May require multiple visits',
    direct: 'Direct and immediate',
  },
  {
    factor: 'Follow-up Care',
    traditional: 'Varies by provider',
    direct: 'Included',
  },
  {
    factor: 'Communication',
    traditional: 'Through multiple parties',
    direct: 'Direct with fabricator',
  },
];

const BENEFITS = [
  {
    icon: DollarSign,
    title: 'Lower Overall Cost',
    description: 'Direct care eliminates unnecessary referral costs and lab markups.'
  },
  {
    icon: Clock,
    title: 'Faster Turnaround',
    description: 'Fewer appointments and no waiting for third-party labs.'
  },
  {
    icon: Users,
    title: 'Direct Communication',
    description: 'Work directly with the professional who creates your dentures.'
  },
  {
    icon: Building,
    title: 'No Middlemen',
    description: 'One provider handles everything from consultation to final fitting.'
  },
];

export default function CostComparisonPage() {
  const [showAppointment, setShowAppointment] = useState(false);

  return (
    <div className="pt-24 lg:pt-32" data-testid="cost-comparison-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Why Going Direct Saves Money
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Understand the cost differences between traditional dental paths and direct denturist care.
          </p>
        </div>
      </section>

      {/* Path Comparison */}
      <section className="py-24 bg-white" data-testid="path-comparison-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
            {/* Traditional Path */}
            <div className="bg-slate-50 rounded-3xl p-8 lg:p-12">
              <div className="inline-flex items-center gap-2 bg-slate-200 text-slate-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Traditional Path
              </div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Through Multiple Providers
              </h2>
              <div className="mt-8 space-y-4">
                {['Dentist Consultation', 'Referral to Specialist', 'Lab Fabrication', 'Multiple Appointments', 'Follow-up Visits'].map((step, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-sm font-medium">
                      {idx + 1}
                    </div>
                    <span className="text-slate-600">{step}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Each step adds time and cost to the process.
                </p>
              </div>
            </div>

            {/* Direct Path */}
            <div className="bg-orange-50 rounded-3xl p-8 lg:p-12 border-2 border-orange-200">
              <div className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                Direct Denturist
              </div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Streamlined Care
              </h2>
              <div className="mt-8 space-y-4">
                {['Consultation', 'Impressions & Fabrication', 'Fitting', 'Adjustments', 'Ongoing Support'].map((step, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-orange-600 flex items-center justify-center text-white text-sm font-medium">
                      {idx + 1}
                    </div>
                    <span className="text-slate-700 font-medium">{step}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-orange-200">
                <p className="text-sm text-orange-700 font-medium">
                  All handled by one professional = significant cost savings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-slate-50" data-testid="comparison-table-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Side-by-Side Comparison
            </h2>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Factor</TableHead>
                  <TableHead className="font-semibold">Traditional Path</TableHead>
                  <TableHead className="font-semibold text-orange-600">Direct Denturist</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {COMPARISON_DATA.map((row, idx) => (
                  <TableRow key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <TableCell className="font-medium text-slate-900">{row.factor}</TableCell>
                    <TableCell className="text-slate-600">{row.traditional}</TableCell>
                    <TableCell className="text-orange-600 font-medium">{row.direct}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Direct Care Advantage
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {BENEFITS.map((benefit, idx) => (
              <div key={idx} className="bg-slate-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {benefit.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-600">
            Patients across Coos Bay, Florence, and Roseburg frequently report significant cost savings compared to multi-provider referral models. Actual savings vary based on individual treatment needs.
          </p>
          <p className="mt-4 text-sm text-slate-500">
            Note: We do not perform extractions or implants. These services require a separate provider.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600" data-testid="cost-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Request a Cost Comparison Consultation
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Get transparent pricing and see how direct care can save you money.
          </p>
          <Button
            onClick={() => setShowAppointment(true)}
            className="mt-10 bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all hover:scale-105"
            data-testid="cost-schedule-btn"
          >
            Schedule Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
